import { useEffect, useRef, useState } from 'react'
import { Alvo, prepararAbertura } from './abrir'
import { baixarXlsx, exportarParaExcel } from './exportarExcel'
import ConfirmarAbertura from './ConfirmarAbertura'
import { buscarImagemDoPortal, criarCarregadorDeImagens, FotoBaixada } from './imagens'

export interface ContextoRelatorio {
  cliente: string
  corPrincipal: string
  atualizadoEm: string | null
  usuarioNome: string
  fonte: string
  /** Cores dos gráficos, do template do relatório. */
  paleta?: string[] | null
  /** Os sites que o Portal.abrir({ tipo: 'link' }) pode abrir. Vazio ou ausente: nenhum. */
  linksLiberados?: string[]
}

// Pedidos de abertura (Portal.abrir) por minuto, por relatório aberto.
const MAX_ABERTURAS_POR_MINUTO = 20

// Exportações para Excel por minuto, por relatório aberto: cada uma monta uma planilha de até 50 mil linhas.
const MAX_EXPORTACOES_POR_MINUTO = 6

export interface ResultadoConsulta {
  linhas: Array<Record<string, unknown>>
  fonte?: string
}

interface Props {
  /** O documento já preparado pelo servidor: CSP e SDK no início do head. */
  html: string
  contexto: ContextoRelatorio
  /** Quem responde as consultas do relatório: o servidor, sempre. */
  consultar: (consulta: unknown) => Promise<ResultadoConsulta>
  altura?: string | number
  /** Aba Dados: o relatório em modo editor avisa o clique num bloco ou num texto. */
  aoSelecionar?: (escolha: { bloco: string | null; texto: string | null; filtros?: boolean }) => void
  /** Bloco contornado na prévia, o que está aberto no painel. */
  destacar?: string | null
  /** Modo editor: o relatório conta cada consulta, pelo bloco ou solta. */
  aoUso?: (uso: UsoNoRelatorio) => void
  /**
   * Onde guardar os filtros da tela que a pessoa marcou, para a próxima abertura. O relatório
   * não tem armazenamento próprio (origem opaca), então quem guarda é o portal. Sem a chave,
   * não guarda (prévia do editor).
   */
  chaveFiltros?: string
  /** Quem busca a foto que o relatório pede com Portal.imagem. O padrão pede ao servidor do portal. */
  buscarImagem?: (url: string) => Promise<FotoBaixada>
  /**
   * Filtros da tela que o portal marca ao abrir, por cima do que a pessoa guardou (a loja escolhida no seletor). Lista vazia
   * limpa o filtro. Chegam ao relatório como se a pessoa os tivesse marcado: a barra mostra e ela pode trocar.
   */
  filtrosAplicados?: Record<string, Array<string | number | boolean>>
  /**
   * O relatório abriu algo pelo Portal.abrir (depois de abrir de fato) ou exportou um bloco para Excel (tipo "exportar", destino com o bloco e
   * a quantidade de linhas): quem usa o relatório registra no log de ações.
   */
  aoAbrir?: (acao: { tipo: string; destino: string }) => void
  /** Quem entrega a planilha ao navegador. O padrão baixa o arquivo; os testes trocam. */
  baixarPlanilha?: (arquivo: string, dados: ArrayBuffer) => void
}

const lerFiltros = (chave?: string) => {
  if (!chave) return null
  try {
    const v = JSON.parse(window.localStorage.getItem(chave) || 'null')
    return v && typeof v === 'object' && !Array.isArray(v) ? v : null
  } catch {
    return null
  }
}

// Só listas de texto ou número, e com teto: é dado que veio do iframe.
const guardarFiltros = (chave: string, selecao: unknown) => {
  if (!selecao || typeof selecao !== 'object' || Array.isArray(selecao)) return
  const limpo: Record<string, Array<string | number | boolean>> = {}
  Object.entries(selecao as Record<string, unknown>).slice(0, 30).forEach(([k, v]) => {
    if (Array.isArray(v)) limpo[k.slice(0, 60)] = v.filter(x => typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean').slice(0, 100)
  })
  try {
    const texto = JSON.stringify(limpo)
    if (texto.length < 50000) window.localStorage.setItem(chave, texto)
  } catch { /* armazenamento cheio ou bloqueado: só não guarda */ }
}

export type UsoNoRelatorio =
  | { tipo: 'bloco-usado'; bloco: string }
  | { tipo: 'consulta-solta'; colunas: string[]; medidas: string[] }

/**
 * Roda o relatório HTML isolado do portal.
 *
 * O iframe tem sandbox só com allow-scripts — sem allow-same-origin. A origem do
 * documento vira opaca: ele não lê o DOM do portal, nem cookie, nem localStorage, nem
 * navega a página de cima. A política CSP que o servidor pôs no head proíbe qualquer
 * rede, então o único caminho de dados é esta ponte, e toda consulta passa pelo
 * servidor, que confere e filtra.
 *
 * Só aceita mensagem cujo remetente seja o próprio iframe: outra janela não consegue
 * se passar pelo relatório. A resposta vai com destino "*" porque a origem opaca não
 * tem nome para pôr ali.
 */
const RelatorioHtmlFrame = ({ html, contexto, consultar, altura = '100%', aoSelecionar, destacar, aoUso, chaveFiltros, buscarImagem, filtrosAplicados, aoAbrir, baixarPlanilha }: Props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Guarda as versões mais novas sem registrar o ouvinte de novo a cada render: um
  // ouvinte trocado no meio de uma consulta perderia a resposta.
  const ultimo = useRef({ contexto, consultar, aoSelecionar, destacar, aoUso, chaveFiltros, filtrosAplicados, aoAbrir, baixarPlanilha })
  ultimo.current = { contexto, consultar, aoSelecionar, destacar, aoUso, chaveFiltros, filtrosAplicados, aoAbrir, baixarPlanilha }

  // Portal.abrir: a pergunta aberta (a pessoa ainda não tocou em Abrir nem em Cancelar) e a conta dos pedidos do último minuto.
  const perguntaRef = useRef<{ id: number; alvo: Alvo } | null>(null)
  const [pergunta, setPergunta] = useState<{ id: number; alvo: Alvo } | null>(null)
  const aberturas = useRef<number[]>([])
  const exportacoes = useRef<number[]>([])

  /** Fecha a pergunta e conta ao relatório se abriu (true) ou se a pessoa desistiu (false). */
  const encerrarPergunta = (aberto: boolean) => {
    const p = perguntaRef.current
    if (!p) return
    perguntaRef.current = null
    setPergunta(null)
    iframeRef.current?.contentWindow?.postMessage({ portal: true, tipo: 'abrir-resultado', id: p.id, ok: true, aberto }, '*')
    if (aberto) ultimo.current.aoAbrir?.(p.alvo.registro)
  }

  // As fotos pedidas pelo relatório: guardadas, sem pedido repetido e poucas por vez. Uma vez por montagem do frame.
  const buscarImagemRef = useRef(buscarImagem)
  buscarImagemRef.current = buscarImagem
  const fotos = useRef(criarCarregadorDeImagens({ buscar: url => (buscarImagemRef.current ?? buscarImagemDoPortal)(url) }))

  // Sobe a cada documento novo: resposta de consulta do documento anterior não entra no atual
  // (o SDK recomeça a numeração das consultas a cada documento).
  const geracao = useRef(0)

  // Trava contra navegação: o documento que o portal montou carrega uma vez só. Outro "load"
  // depois de o SDK ter avisado que está pronto quer dizer que a página trocou de endereço (o
  // que a CSP não impede, e o endereço pode levar dados). Aí o relatório sai da tela na hora,
  // para a página de fora não ficar dentro do portal.
  const carga = useRef({ pronto: false, vezes: 0 })
  const [fechado, setFechado] = useState(false)
  useEffect(() => {
    geracao.current++
    carga.current = { pronto: false, vezes: 0 }
    setFechado(false)
    // A pergunta era do documento anterior: ele não está mais lá para receber a resposta.
    perguntaRef.current = null
    setPergunta(null)
  }, [html])

  const aoCarregar = () => {
    carga.current.vezes++
    if (carga.current.pronto && carga.current.vezes > 1) {
      setFechado(true)
      return
    }
    enviarDestaque()
  }

  const enviarDestaque = () =>
    iframeRef.current?.contentWindow?.postMessage({ portal: true, tipo: 'destacar', bloco: ultimo.current.destacar ?? null }, '*')

  useEffect(() => {
    enviarDestaque()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destacar])

  useEffect(() => {
    const aoReceber = async (e: MessageEvent) => {
      const alvo = iframeRef.current?.contentWindow
      const m = e.data
      if (!alvo || e.source !== alvo || !m || m.portal !== true) return

      if (m.tipo === 'pronto') {
        carga.current.pronto = true
        // Conveniência para o relatório ler ?uf=SP da URL do portal. Não é segurança:
        // os filtros que valem são os que o servidor aplica.
        const filtrosUrl = Object.fromEntries(new URLSearchParams(window.location.search))
        const filtrosSalvos = { ...lerFiltros(ultimo.current.chaveFiltros), ...ultimo.current.filtrosAplicados }
        alvo.postMessage({ portal: true, tipo: 'contexto', contexto: { ...ultimo.current.contexto, filtrosUrl, filtrosSalvos } }, '*')
      } else if (m.tipo === 'filtros') {
        if (ultimo.current.chaveFiltros) guardarFiltros(ultimo.current.chaveFiltros, m.selecao)
      } else if (m.tipo === 'selecionar') {
        ultimo.current.aoSelecionar?.({ bloco: typeof m.bloco === 'string' ? m.bloco : null, texto: typeof m.texto === 'string' ? m.texto : null, filtros: m.filtros === true })
      } else if (m.tipo === 'bloco-usado' && typeof m.bloco === 'string') {
        ultimo.current.aoUso?.({ tipo: 'bloco-usado', bloco: m.bloco })
      } else if (m.tipo === 'consulta-solta') {
        const lista = (x: unknown) => (Array.isArray(x) ? x.map(String) : [])
        ultimo.current.aoUso?.({ tipo: 'consulta-solta', colunas: lista(m.colunas), medidas: lista(m.medidas) })
      } else if (m.tipo === 'abrir') {
        if (typeof m.id !== 'number') return
        const recusar = (erro: string) => alvo.postMessage({ portal: true, tipo: 'abrir-resultado', id: m.id, ok: false, erro }, '*')

        let destino: Alvo
        try {
          destino = prepararAbertura(m.destino, ultimo.current.contexto.linksLiberados || [])
        } catch (err: any) {
          recusar(err?.message || 'Não foi possível abrir')
          return
        }

        const agora = Date.now()
        while (aberturas.current.length && agora - aberturas.current[0] > 60_000) aberturas.current.shift()
        if (aberturas.current.length >= MAX_ABERTURAS_POR_MINUTO) { recusar('Muitos pedidos seguidos; tente em instantes'); return }
        aberturas.current.push(agora)

        // Um pedido novo cancela a pergunta que ainda estava aberta.
        encerrarPergunta(false)

        // Se o navegador diz que a pessoa acabou de tocar, abre direto. Sem toque recente, ou se o navegador bloquear (iPhone),
        // o portal pergunta, e o toque em Abrir é um toque de verdade. Ligação e site fora da lista do BI sempre passam pela pergunta.
        const comToque = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation?.isActive === true
        if (!destino.confirmar && comToque) {
          // Sem "noopener" no pedido para saber se abriu (com ele o retorno é sempre null); o vínculo é cortado logo depois.
          const janela = window.open(destino.url, '_blank')
          if (janela) {
            janela.opener = null
            alvo.postMessage({ portal: true, tipo: 'abrir-resultado', id: m.id, ok: true, aberto: true }, '*')
            ultimo.current.aoAbrir?.(destino.registro)
            return
          }
        }
        perguntaRef.current = { id: m.id, alvo: destino }
        setPergunta({ id: m.id, alvo: destino })
      } else if (m.tipo === 'exportar') {
        // Portal.exportar / data-exportar: o relatório manda as colunas e as linhas do bloco; o portal confere, monta o .xlsx e baixa.
        if (typeof m.id !== 'number') return
        const daVez = geracao.current
        const responder = (r: Record<string, unknown>) => { if (daVez === geracao.current) alvo.postMessage({ portal: true, tipo: 'exportar-resultado', id: m.id, ...r }, '*') }
        const agora = Date.now()
        while (exportacoes.current.length && agora - exportacoes.current[0] > 60_000) exportacoes.current.shift()
        if (exportacoes.current.length >= MAX_EXPORTACOES_POR_MINUTO) { responder({ ok: false, erro: 'Muitas exportações seguidas; tente em instantes' }); return }
        exportacoes.current.push(agora)
        try {
          const feito = await exportarParaExcel(m, ultimo.current.baixarPlanilha ?? baixarXlsx)
          responder({ ok: true, linhas: feito.linhas })
          ultimo.current.aoAbrir?.({ tipo: 'exportar', destino: `${feito.bloco} · ${feito.linhas} linhas`.slice(0, 80) })
        } catch (err: any) {
          responder({ ok: false, erro: err?.message || 'Não foi possível exportar' })
        }
      } else if (m.tipo === 'imagem') {
        // Dado que veio do iframe: só id numérico e endereço de texto curto. Quem confere o endereço é o servidor.
        if (typeof m.id !== 'number' || typeof m.url !== 'string' || m.url.length === 0 || m.url.length > 2000) return
        const daVez = geracao.current
        try {
          const foto = await fotos.current(m.url)
          if (daVez !== geracao.current) return
          alvo.postMessage({ portal: true, tipo: 'imagem-resultado', id: m.id, ok: true, bytes: foto.bytes, mime: foto.mime }, '*', [foto.bytes])
        } catch (err: any) {
          if (daVez !== geracao.current) return
          alvo.postMessage({ portal: true, tipo: 'imagem-resultado', id: m.id, ok: false, erro: err?.message || 'Foto indisponível' }, '*')
        }
      } else if (m.tipo === 'consultar') {
        const daVez = geracao.current
        try {
          const r = await ultimo.current.consultar(m.consulta)
          if (daVez !== geracao.current) return
          alvo.postMessage({ portal: true, tipo: 'resultado', id: m.id, ok: true, linhas: r.linhas, fonte: r.fonte }, '*')
        } catch (err: any) {
          if (daVez !== geracao.current) return
          alvo.postMessage({ portal: true, tipo: 'resultado', id: m.id, ok: false, erro: err?.message || 'Falha ao consultar os dados' }, '*')
        }
      }
    }

    window.addEventListener('message', aoReceber)
    return () => window.removeEventListener('message', aoReceber)
  }, [])

  if (fechado) {
    return (
      <div role="alert" style={{ padding: 24, color: '#8f2a21', background: '#fdecec', borderRadius: 12, fontSize: 14, lineHeight: 1.5 }}>
        Este relatório tentou abrir outra página e foi fechado por segurança. Avise o time de BI responsável por ele.
      </div>
    )
  }

  return (
    <>
      <iframe
        ref={iframeRef}
        title="Relatório"
        sandbox="allow-scripts"
        srcDoc={html}
        // O documento recarrega a cada troca; o destaque volta quando ele termina de abrir.
        onLoad={aoCarregar}
        style={{ width: '100%', height: altura, border: 0, background: '#fff', display: 'block' }}
      />
      {pergunta && (
        <ConfirmarAbertura alvo={pergunta.alvo} aoAbrir={() => encerrarPergunta(true)} aoCancelar={() => encerrarPergunta(false)} />
      )}
    </>
  )
}

export default RelatorioHtmlFrame
